/*
    Service that manages the state of the new/edit analysis window
*/
import { Injectable } from '@angular/core';
import { Card, Variable, CorrelationMatrixCard, PValueAdjustmentMethod, UnitRootTestADF, UnitRootTestZA, UnitRootTestKPSS, PACF, STLDecomposition, STLDecompositionCard } from 'src/generated-sources';
import { map } from 'rxjs/operators';
import { AnalysisType } from './enums';
import { randomId } from '@utils/random';
import { CardWizardVariable } from '@features/eda/card-models';
import { SampleContextService } from '@features/eda/sample-context.service';

interface AvailableVariableOptions {
    isTestVariable?: boolean;
    isSeriesVariable?: boolean;
    isTimeVariable?: boolean;
}

@Injectable()
export class CardWizardService {
    constructor(
        private sampleContextService: SampleContextService
    ) {}

    private mapVariables(variables: Variable[], type: string, options?: AvailableVariableOptions): CardWizardVariable[] {
        return variables.map((variable: Variable) => ({
            ...variable,
            disabled: this.isVariableDisabled(variable, type, options)
        })).sort((a, b) => (+a.disabled - +b.disabled));
    }

    availableVariables(type: string, options?: AvailableVariableOptions) {
        return this.sampleContextService.availableVariables().pipe(
            map(variables => this.mapVariables(variables, type, options))
        );
    }

    private isVariableDisabled(variable: Variable, type: string, options?: AvailableVariableOptions) {
        const analysisType = this.getAnalysisType(type);

        switch (analysisType) {
            case AnalysisType.UNIVARIATE_CARD:
            case AnalysisType.MULTIVARIATE_CARD:
                return variable.type !== Variable.Type.CONTINUOUS;
            case AnalysisType.BIVARIATE_CARD:
                if (type === 'chi2_independence_test') {
                    return variable.type !== Variable.Type.CATEGORICAL;
                } else if (type === 'fit_2d_distribution' || type === 'fit_curve') {
                    return variable.type !== Variable.Type.CONTINUOUS;
                } else {
                    if (options && options.isTestVariable) {
                        return variable.type !== Variable.Type.CONTINUOUS;
                    }
                }
                break;
            case AnalysisType.TRIVARIATE_CARD:
                if (type === 'scatter_plot_3d') {
                    return variable.type !== Variable.Type.CONTINUOUS;
                }
                break;
            case AnalysisType.TIME_SERIES_CARD:
                if (options?.isSeriesVariable) {
                    return variable.type !== Variable.Type.CONTINUOUS;
                }

                if (options?.isTimeVariable) {
                    return variable.type !== Variable.Type.CATEGORICAL;
                }
                break;
        }

        return false;
    }

    getAnalysisType(type: string) {
        switch (type) {
            case 'univariate_header':
                return AnalysisType.UNIVARIATE_HEADER;
            case 'bivariate_header':
                return AnalysisType.BIVARIATE_HEADER;
            case 'scatter_plot_3d':
                return AnalysisType.TRIVARIATE_CARD;
            case 'parallel_coordinates_plot':
            case 'pca':
            case 'correlation_matrix':
                return AnalysisType.MULTIVARIATE_CARD;
            case 'fit_2d_distribution':
            case 'fit_curve':
            case 'mood_test_2samp':
            case 'mood_nsamp':
            case 'ks_test_2samp':
            case 'ttest_2samp':
            case 'oneway_anova':
            case 'chi2_independence_test':
            case 'pairwise_mood':
            case 'pairwise_ttest':
                return AnalysisType.BIVARIATE_CARD;
            case 'fit_distribution':
            case 'shapiro':
            case 'sign_test_1samp':
            case 'ttest_ztest_1samp':
                return AnalysisType.UNIVARIATE_CARD;
            case 'unit_root_test_adf':
            case 'unit_root_test_za':
            case 'unit_root_test_kpss':
            case 'mann_kendall_test':
            case 'durbin_watson':
            case 'acf_plot':
            case 'stl_decomposition':
                return AnalysisType.TIME_SERIES_CARD;
            default:
                return '';
        }
    }

    getCardDefault(type: string) {
        const analysisType = this.getAnalysisType(type);
        const analysisDefault = this.getAnalysisDefault(analysisType);
        let card = {
            type,
            ...analysisDefault,
            id: randomId()
        };
        let defaultOptions: Partial<Card> = {};

        switch (type) {
            case 'univariate_header':
                defaultOptions = {
                    showBoxPlot: true,
                    showFrequencyTable: true,
                    showHistogram: true,
                    showQuantile: true,
                    showSummary: true,
                    showCumulativeDistributionFunction: false,
                };
                break;
            case 'bivariate_header':
                defaultOptions = {
                    showBoxPlot: true,
                    showFrequencyTable: true,
                    showHistogram: true,
                    showMosaicPlot: true,
                    showScatterPlot: true,
                    showSummary: true
                };
                break;
            case 'correlation_matrix':
                defaultOptions = {
                    metric: CorrelationMatrixCard.CorrelationMetric.SPEARMAN,
                    heatmapParams: {
                        filterVariablesWithoutValues: false,
                        showValues: true,
                        showColors: true,
                        showAbsValues: false,
                        threshold: 0
                    }
                };
                break;
            case 'fit_distribution':
                defaultOptions = {
                    distributions: [{
                        distribution: {
                            type: 'normal'
                        }
                    }]
                };
                break;
            case 'fit_2d_distribution':
                defaultOptions = {
                    distribution: {
                        type: 'kde_2d',
                        x_relative_bandwidth: 15,
                        y_relative_bandwidth: 15
                    },
                    xResolution: 640,
                    yResolution: 480
                };
                break;
            case 'fit_curve':
                // look for option with id named 'type'
                defaultOptions = {
                    curves: [{
                        type: 'polynomial',
                        degree: 2
                    }]
                };
                break;
            case 'pca':
                defaultOptions = {
                    heatmapParams: {
                        filterVariablesWithoutValues: false,
                        showValues: true,
                        showColors: true,
                        showAbsValues: false,
                        threshold: 0
                    },
                    showScatterPlot: true,
                    showLoadingPlot: true,
                    showScreePlot: true,
                    showComponents: true
                };
                break;
            case 'scatter_plot_3d':
                defaultOptions = {
                    symbolSize: 4,
                    maxNumberOfPoints: 100_000,
                };
                break;
            case 'pairwise_mood':
            case 'pairwise_ttest':
                defaultOptions = {
                    adjustmentMethod: PValueAdjustmentMethod.NONE,
                    maxGroups: 10
                };
                break;
            case 'mood_nsamp':
            case 'oneway_anova':
                defaultOptions = {
                    maxGroups: 10
                };
                break;
            case 'chi2_independence_test':
                defaultOptions = {
                    maxValuesX: 5,
                    maxValuesY: 5
                };
                break;
            case 'parallel_coordinates_plot':
                defaultOptions = {
                    maxNumberOfPoints: 10_000,
                };
                break;
            case 'ks_test_2samp':
            case 'mood_test_2samp':
            case 'ttest_2samp':
                defaultOptions = {
                    // Setting a non null value for these fields will select
                    // the manual mode for defining the populations by default.
                    value1: [],
                    value2: [],
                }
                break;
            case 'shapiro':
            case 'sign_test_1samp':
            case 'ttest_ztest_1samp':
                break;
            case 'unit_root_test_adf':
                defaultOptions = {
                    regressionMode: UnitRootTestADF.RegressionMode.CONSTANT_ONLY,
                };
                break;
            case 'unit_root_test_za':
                defaultOptions = {
                    regressionMode: UnitRootTestZA.RegressionMode.CONSTANT_ONLY,
                };
                break;
            case 'unit_root_test_kpss':
                defaultOptions = {
                    regressionMode: UnitRootTestKPSS.RegressionMode.CONSTANT,
                };
                break;
            case 'acf_plot':
                defaultOptions = {
                    showSummary: true,
                    adjusted: false,
                    pacfMethod: PACF.Method.LEVINSON_DURBIN,
                };
                break;
            case 'stl_decomposition':
                defaultOptions = {
                    params: {
                        seasonal: 7,
                        decompositionType: STLDecomposition.DecompositionType.ADDITIVE,
                        robust: true,
                        seasonalJump: 1,
                        trendJump: 1,
                        lowPassJump: 1,
                        seasonalDeg: STLDecomposition.DegreeMode.CONSTANT,
                        trendDeg: STLDecomposition.DegreeMode.CONSTANT,
                        lowPassDeg: STLDecomposition.DegreeMode.CONSTANT,
                        showSummary: true,
                    },
                    showCompactChart: false,
                    dataZoomStart: 0,
                    dataZoomEnd: 100,
                    selectedSeries: Object.values(STLDecompositionCard.SeriesKind),
                };
                break;
        }

        card = {
            ...card,
            ...defaultOptions
        };

        return card;
    }

    getAnalysisDefault(analysisType: string): Partial<Card> {
        switch (analysisType) {
            case AnalysisType.UNIVARIATE_HEADER:
            case AnalysisType.BIVARIATE_HEADER:
                return {
                    xColumns: [],
                };
            case AnalysisType.MULTIVARIATE_CARD:
                return {
                    columns: []
                };
        }

        return {};
    }
}
